import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, TextField, Button, Typography,
  Chip, Box, CircularProgress, InputAdornment, IconButton, Paper,
  Alert, Snackbar, AppBar, Toolbar, Badge, Pagination, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,Drawer,
  List, ListItem, ListItemText, ListItemAvatar, Avatar
} from '@mui/material';
import {
  Search, Add, Remove, ArrowForward, LocalPharmacy, ShoppingCart,
  ArrowBack, CheckCircle, Upload, Close, Visibility, ChevronLeft,InfoOutlined
} from '@mui/icons-material';
import PatientDetailsForm from "../BookingPages/PatientDetailsForm";
import { apiRequest } from '../../fetchSetup';
import { Divider } from '@mui/material';


const MedicineListScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userEmail, orderType } = location.state || {};

  const [medicines, setMedicines] = useState([]);
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showCart, setShowCart] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  // Patient form states - with persistence
  const [showPatientForm, setShowPatientForm] = useState(() => {
    const savedPatientDetails = sessionStorage.getItem('pharmacyPatientDetails');
    return !savedPatientDetails;
  });

  const [patientDetails, setPatientDetails] = useState(() => {
    const savedDetails = sessionStorage.getItem('pharmacyPatientDetails');

    if (!savedDetails || savedDetails === "undefined") {
      return null;
    }
    return savedDetails ? JSON.parse(savedDetails) : null;
  });
  const [offeredDiscountPct, setOfferedDiscountPct] = useState(null);
  const [cart, setCart] = useState(() => {
    if (orderType === "pharmacy" || orderType === "medicine") {
      const savedCart = sessionStorage.getItem('pharmacyCart');
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          if (parsedCart && Array.isArray(parsedCart) && parsedCart.length > 0) {
            const validMedicines = parsedCart.filter(item => item.medicine_id);
            if (validMedicines.length > 0) {
              console.log('✅ Medicine cart restored from sessionStorage:', validMedicines);
              return validMedicines;
            }
          }
        } catch (error) {
          console.error('Error parsing saved cart:', error);
        }
      }
    }
    sessionStorage.removeItem('diagnosticOrderData');
    return [];
  });

  // ─── AYUJA pricing state ───────────────────────────────────────────────────
  const [ayujaCartPrices, setAyujaCartPrices] = useState({});
  const [ayujaSummary, setAyujaSummary] = useState(null);
  const lastFetchedCartKey = useRef('');
  const pricingPollRef = useRef(null);

  // Prescription upload states
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [prescriptionFile, setPrescriptionFile] = useState(() => {
    const savedPrescription = sessionStorage.getItem('pharmacyPrescriptionFile');
    return savedPrescription ? savedPrescription : null;
  });
  const [prescriptionPreview, setPrescriptionPreview] = useState(() => {
    const savedPreview = sessionStorage.getItem('pharmacyPrescriptionPreview');
    return savedPreview || null;
  });

  const categories = ['All', 'Tablet', 'Syrup', 'Injection', 'Capsules', 'Cream', 'Drop'];

  // ─── AYUJA fetch: single effect, no setCart() inside ──────────────────────
  useEffect(() => {
    if (cart.length === 0) {
      setAyujaCartPrices({});
      setAyujaSummary(null);
      lastFetchedCartKey.current = '';
      sessionStorage.removeItem('ayujaPricing');
      return;
    }

    const cartKey = cart
      .map(i => `${i.medicine_id}:${i.quantity}`)
      .sort()
      .join('|');

    if (cartKey === lastFetchedCartKey.current) return;

    const fetchAyujaPrices = async () => {
      try {
        const response = await apiRequest(
          `${process.env.REACT_APP_MEDICINE_API_URL}/calculate-order-price`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: cart.map(i => ({
                medicine_id: i.medicine_id,
                quantity: i.quantity,
              })),
            }),
          }
        );

        if (!response.ok) {
          console.error('AYUJA fetch failed:', response.status, await response.text());
          return;
        }

        const data = await response.json();
        console.log('AYUJA response:', data);

        const priceMap = {};
        if (data.item_breakdown) {
          data.item_breakdown.forEach(item => {
            priceMap[item.medicine_id] = item.selling_price;
          });
        }

        setAyujaCartPrices(priceMap);
        setAyujaSummary(data);

        sessionStorage.setItem(
          'ayujaPricing',
          JSON.stringify({
            item_breakdown: data.item_breakdown,
            final_discount_pct: data.final_discount_pct,
            alpha: data.alpha,
            ayuja_applied: data.ayuja_applied,
            ayuja_subtotal: data.ayuja_subtotal,
            mrp_total: data.mrp_total,
          })
        );
        lastFetchedCartKey.current = cartKey;
      } catch (err) {
        console.error('AYUJA fetch error:', err);
      }
    };

    fetchAyujaPrices();
  }, [cart]);


  useEffect(() => {
    const POLL_EVERY = 10 * 1000;
    const CACHE_KEY = 'ayujaPricingConfig';

    const fetchPricingConfig = async (bustCache = false) => {
      if (!bustCache) {
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed._cachedAt || 0);
            if (age < POLL_EVERY && typeof parsed.configured_offered_discount_pct === 'number') {
              setOfferedDiscountPct(prev =>
                prev !== parsed.configured_offered_discount_pct
                  ? parsed.configured_offered_discount_pct
                  : prev
              );
              return;
            }
          }
        } catch { }
      }
      try {
        const response = await apiRequest(
          `${process.env.REACT_APP_MEDICINE_API_URL}/pricing-config`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );
        if (!response.ok) {
          setOfferedDiscountPct(prev => prev ?? 20);
          return;
        }
        const data = await response.json();
        const newPct = data.configured_offered_discount_pct ?? 20;
        setOfferedDiscountPct(prev => {
          if (prev !== newPct) return newPct;
          return prev;
        });
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ...data, _cachedAt: Date.now() }));
      } catch (err) {
        setOfferedDiscountPct(prev => prev ?? 20);
      }
    };

    fetchPricingConfig(false);
    pricingPollRef.current = setInterval(() => fetchPricingConfig(true), POLL_EVERY);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchPricingConfig(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(pricingPollRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  useEffect(() => {
  if (showCart) {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  return () => {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  };
}, [showCart]);
  useEffect(() => {
    if (cart.length > 0) {
      const validCart = cart.filter(item => item.medicine_id && (item.price || item.discounted_price));
      if (validCart.length > 0) {
        sessionStorage.setItem('pharmacyCart', JSON.stringify(validCart));
        console.log('Valid medicine cart saved:', validCart.length, 'items');
      }
    } else {
      sessionStorage.removeItem('pharmacyCart');
      console.log('Empty cart cleared from sessionStorage');
    }
  }, [cart]);

  useEffect(() => {
    fetchMedicines();
  }, []);

  useEffect(() => {
    filterMedicines();
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, medicines]);

  useEffect(() => {
    if (orderType === "pharmacy" || orderType === "medicine") {
      sessionStorage.removeItem('diagnosticOrderData');
      console.log('🧹 Cleared diagnostic data when entering medicine list');
    }
  }, [orderType]);

  const fetchMedicines = async () => {
    try {
      const response = await apiRequest(process.env.REACT_APP_GET_MEDICINES);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.medicines && Array.isArray(data.medicines)) {
        const activeMedicines = data.medicines.filter(med => med.is_active !== false);
        setMedicines(activeMedicines);
        setFilteredMedicines(activeMedicines);
      } else {
        console.error('Invalid data format:', data);
        showSnackbar('Invalid data format received', 'error');
      }
    } catch (error) {
      console.error('Error fetching medicines:', error);
      showSnackbar(`Failed to load medicines: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterMedicines = () => {
    let filtered = medicines;
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(med => med.drug_type === selectedCategory);
    }
    if (searchQuery) {
      filtered = filtered.filter(med =>
        med.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        med.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        med.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    setFilteredMedicines(filtered);
  };

  const totalPages = Math.ceil(filteredMedicines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentMedicines = filteredMedicines.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const addToCart = (medicine) => {
    const existingItem = cart.find(item => item.medicine_id === medicine.medicine_id);
    let updatedCart;
    if (existingItem) {
      updatedCart = cart.map(item =>
        item.medicine_id === medicine.medicine_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [...cart, { ...medicine, quantity: 1 }];
    }
    setCart(updatedCart);
    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
    if (medicine.stock > 0) showSnackbar('Added to cart', 'success');
  };

  const removeFromCart = (medicineId) => {
    const existingItem = cart.find(item => item.medicine_id === medicineId);
    let updatedCart;
    if (existingItem.quantity === 1) {
      updatedCart = cart.filter(item => item.medicine_id !== medicineId);
      showSnackbar('Removed from cart', 'info');
    } else {
      updatedCart = cart.map(item =>
        item.medicine_id === medicineId
          ? { ...item, quantity: item.quantity - 1 }
          : item
      );
    }
    setCart(updatedCart);
    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
  };

  const getCartQuantity = (medicineId) => {
    const item = cart.find(item => item.medicine_id === medicineId);
    return item ? item.quantity : 0;
  };

  const getTotalItems = () => cart.reduce((total, item) => total + item.quantity, 0);

  const getMrpTotal = () =>
    cart.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);

  const getSellingTotal = () =>
    cart.reduce((s, i) => {
      const ayujaPrice = ayujaCartPrices[i.medicine_id];
      return s + (ayujaPrice !== undefined ? ayujaPrice : (i.discounted_price || i.price || 0)) * i.quantity;
    }, 0);

  const navigateToCheckout = (details) => {
    const cartWithAyujaPrices = cart.map(item => ({
      ...item,
      discounted_price:
        ayujaCartPrices[item.medicine_id] !== undefined
          ? ayujaCartPrices[item.medicine_id]
          : (item.discounted_price || item.price),
    }));

    const sellingTotal = getSellingTotal();

    sessionStorage.removeItem('diagnosticOrderData');
    sessionStorage.setItem('pharmacyCart', JSON.stringify(cartWithAyujaPrices));
    sessionStorage.setItem('pharmacyPatientDetails', JSON.stringify(details || patientDetails));
    if (prescriptionFile) sessionStorage.setItem('pharmacyPrescriptionFile', prescriptionFile);
    if (prescriptionPreview) sessionStorage.setItem('pharmacyPrescriptionPreview', prescriptionPreview);

    navigate('/medicine-delivery-address', {
      state: {
        cart: cartWithAyujaPrices,
        patientDetails: details || patientDetails,
        orderType: orderType || 'pharmacy',
        userEmail,
        totalAmount: sellingTotal,
        prescriptionFile,
        prescriptionPreview,
      },
    });
  };

  const proceedToCheckout = () => {
    if (cart.length === 0) { showSnackbar('Please add medicines to cart', 'warning'); return; }
    const requiresPrescription = cart.some(item => item.prescription_required);
    if (requiresPrescription && !prescriptionFile) {
      showSnackbar('Please upload prescription for medicines that require it', 'warning');
      setShowPrescriptionDialog(true);
      return;
    }
    const validCart = cart.filter(item => item.medicine_id && (item.price || item.discounted_price));
    if (validCart.length === 0) { showSnackbar('Invalid items in cart. Please refresh and try again.', 'error'); return; }
    navigateToCheckout();
  };

  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handlePatientDetailsComplete = (details) => {
    sessionStorage.setItem('pharmacyPatientDetails', JSON.stringify(details));
    setPatientDetails(details);
    setShowPatientForm(false);
    showSnackbar('Patient details saved successfully', 'success');
  };

  const handleBackToPatientForm = () => setShowPatientForm(true);

  const handlePrescriptionFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) { showSnackbar('Please upload a valid image (JPG, PNG) or PDF file', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showSnackbar('File size should not exceed 5MB', 'error'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setPrescriptionFile(base64String);
      setPrescriptionPreview(base64String);
      sessionStorage.setItem('pharmacyPrescriptionFile', base64String);
      sessionStorage.setItem('pharmacyPrescriptionPreview', base64String);
      showSnackbar('Prescription uploaded successfully', 'success');
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePrescription = () => {
    setPrescriptionFile(null);
    setPrescriptionPreview(null);
    sessionStorage.removeItem('pharmacyPrescriptionFile');
    sessionStorage.removeItem('pharmacyPrescriptionPreview');
    showSnackbar('Prescription removed', 'info');
  };

  const handleClosePrescriptionDialog = () => setShowPrescriptionDialog(false);

  const handleClearPatientDetails = () => {
    if (window.confirm('Are you sure you want to clear all patient details? This will also clear your prescription upload.')) {
      setPatientDetails(null);
      sessionStorage.removeItem('pharmacyPatientDetails');
      setPrescriptionFile(null);
      setPrescriptionPreview(null);
      sessionStorage.removeItem('pharmacyPrescriptionFile');
      sessionStorage.removeItem('pharmacyPrescriptionPreview');
      sessionStorage.removeItem('pharmacyDeliveryAddress');
      setShowPatientForm(true);
      showSnackbar('Patient details and prescription cleared', 'info');
      console.log('🧹 Patient details cleared');
    }
  };

  const handleBackNavigation = () => {
    if (showPatientForm) {
      navigate(-1);
    } else {
      setShowPatientForm(true);
    }
  };

  const mrpTotal = getMrpTotal();
  const sellingTotal = getSellingTotal();
  const totalSavings = mrpTotal - sellingTotal;
  const hasAyuja = Object.keys(ayujaCartPrices).length > 0;

  // ─── Loading screen ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <CircularProgress size={60} sx={{ color: '#006d77' }} />
        <Typography sx={{ mt: 2, fontSize: 16, color: '#64748b' }}>Loading medicines...</Typography>
      </Box>
    );
  }

  // ─── Patient form screen ───────────────────────────────────────────────────
  if (showPatientForm) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
        <AppBar position="static" sx={{ bgcolor: '#006d77' }}>
          <Toolbar sx={{ minHeight: 64 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, mx: 'auto' }}>
              <IconButton edge="start" color="inherit" onClick={handleBackNavigation} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Add Patient Details
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Paper elevation={0} sx={{ borderBottom: '1px solid #e2e8f0', py: 2 }}>
          <Container maxWidth="lg">
            <Stepper activeStep={0} alternativeLabel>
              <Step>
                <StepLabel StepIconProps={{ sx: { '&.Mui-active': { color: '#006d77' } } }}>Patient Details</StepLabel>
              </Step>
              <Step><StepLabel>Select Medicines</StepLabel></Step>
              <Step><StepLabel>Checkout</StepLabel></Step>
            </Stepper>
          </Container>
        </Paper>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          <PatientDetailsForm onComplete={handlePatientDetailsComplete} initialData={patientDetails} userEmail={userEmail} orderType={orderType} />
        </Container>
      </Box>
    );
  }

  // ─── Main screen ───────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: cart.length > 0 ? 12 : 2, ml: { xs: 0, sm: '240px' } }}>
      <AppBar position="static" sx={{ bgcolor: '#006d77' }}>
        <Toolbar sx={{ minHeight: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 40, mx: 'auto' }}>
            <IconButton onClick={handleBackNavigation} sx={{ color: '#fff', p: 0.15 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
              Select Medicines
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={() => setShowCart(true)}>
            <Badge badgeContent={getTotalItems()} color="error">
              <ShoppingCart />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Search */}
      <Paper elevation={0} sx={{ bgcolor: '#fff', px: 2, py: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Container maxWidth="lg">
          <TextField
            fullWidth
            placeholder="Search medicines by name, description, or manufacturer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ color: '#006D77' }} /></InputAdornment>,
            }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f1f5f9', borderRadius: 2, '& fieldset': { border: 'none' } } }}
          />
        </Container>
      </Paper>

      {/* Category filter */}
      <Paper elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', py: 2, px: 2 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
            {categories.map((drug_type) => (
              <Chip
                key={drug_type}
                label={drug_type}
                onClick={() => setSelectedCategory(drug_type)}
                sx={{
                  bgcolor: selectedCategory === drug_type ? '#006D77' : '#f1f5f9',
                  color: selectedCategory === drug_type ? '#fff' : '#64748b',
                  fontWeight: 600, px: 2,
                  '&:hover': { bgcolor: selectedCategory === drug_type ? '#005560' : '#e2e8f0' },
                }}
              />
            ))}
          </Box>
        </Container>
      </Paper>

      {/* Info Banner */}
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2">
            <strong>{filteredMedicines.length} medicines</strong> available
            {cart.length > 0 && <strong> • {cart.length} in cart</strong>}
            {filteredMedicines.length > itemsPerPage && <strong> • Page {currentPage} of {totalPages}</strong>}
          </Typography>
        </Alert>
      </Container>

      {/* Patient Details Summary */}
      {patientDetails && (
        <Container maxWidth="lg" sx={{ mt: 3 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f9fafb' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>Patient Details</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={handleClearPatientDetails}
                  sx={{ textTransform: 'none', color: '#ef4444', fontSize: 13, fontWeight: 600, '&:hover': { bgcolor: '#fef2f2' } }}>
                  Clear All
                </Button>
                <Button size="small" onClick={handleBackToPatientForm}
                  sx={{ textTransform: 'none', color: '#006d77', fontSize: 13, fontWeight: 600 }}>
                  Edit Details
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Name</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.name}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Age</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.dob} years</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Gender</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.gender}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>Phone</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.phone}</Typography>
              </Grid>
              {patientDetails.doctorname && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.primary" sx={{ fontSize: 11 }}>Doctor Name</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b' }}>{patientDetails.doctorname}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f9fafb', mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>Prescription</Typography>
                {prescriptionFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} />
                    <Typography variant="body2" sx={{ color: '#16a34a', fontWeight: 600 }}>Prescription uploaded</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#64748b' }}>No prescription uploaded</Typography>
                )}
              </Box>
              <Button
                size="small"
                variant={prescriptionFile ? "outlined" : "contained"}
                onClick={() => setShowPrescriptionDialog(true)}
                sx={{
                  textTransform: 'none', fontSize: 13, fontWeight: 600,
                  ...(prescriptionFile
                    ? { borderColor: '#006d77', color: '#006d77', '&:hover': { borderColor: '#005560', bgcolor: '#f0fdfa' } }
                    : { bgcolor: '#006d77', color: '#fff', '&:hover': { bgcolor: '#005560' } })
                }}
              >
                {prescriptionFile ? 'View/Edit Prescription' : 'Upload Prescription'}
              </Button>
            </Box>
          </Paper>
        </Container>
      )}

      {/* Medicine Grid */}
      <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
        {filteredMedicines.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <LocalPharmacy sx={{ fontSize: 80, color: '#cbd5e1' }} />
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#475569', mt: 2 }}>No medicines found</Typography>
            <Typography sx={{ fontSize: 14, color: '#94a3b8', mt: 0.5 }}>Try adjusting your search or filters</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {currentMedicines.map((medicine) => {
                const quantity = getCartQuantity(medicine.medicine_id);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={medicine.medicine_id}>
                    <Card
                      elevation={0}
                      sx={{
                        width: "360px", height: "380px", display: "flex", flexDirection: "column",
                        borderRadius: 2.5, border: "1px solid #e2e8f0", transition: "all 0.3s ease",
                        bgcolor: "#fff", overflow: "hidden",
                        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 24px rgba(0,0,0,0.08)", borderColor: "#006d77" }
                      }}
                    >
                      {medicine.image_url ? (
                        <Box sx={{ width: '100%', height: "100%", overflow: 'hidden', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e2e8f0' }}>
                          <Box component="img" src={medicine.image_url} alt={medicine.name}
                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                          <Box sx={{ display: 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', bgcolor: '#f1f5f9' }}>
                            <LocalPharmacy sx={{ fontSize: 60, color: '#cbd5e1' }} />
                            <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1 }}>No image available</Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                          <LocalPharmacy sx={{ fontSize: 60, color: '#cbd5e1' }} />
                          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 1 }}>No image available</Typography>
                        </Box>
                      )}

                      <CardContent sx={{ flexGrow: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600, color: '#1e293b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {medicine.name}
                        </Typography>
                        {medicine.selling_unit !=="loose_allowed" && (<Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600, color: '#64748b', mb: 1, display: 'block' }}>
                          Pack of {medicine.pack_size}
                        </Typography>)}
                        {medicine.manufacturer && (
                          <Typography variant="caption" sx={{ fontSize: 12, color: '#64748b', mb: 1, display: 'block' }}>
                            by {medicine.manufacturer}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontSize: 13, color: '#64748b', mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {medicine.description || 'No description available'}
                        </Typography>
                        {medicine.selling_unit=="loose_allowed" && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                bgcolor: "#f0fdf4",
                                px: 1,
                                py: 0.25,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 17,
                                  fontWeight: 800,
                                  color: "#15803d",
                                  lineHeight: 1,
                                }}
                              >
                               ₹{medicine.price.toFixed(2)}
                              </Typography>

                              <Typography
                                component="span"
                                sx={{
                                  ml: 0.3,
                                  fontSize: 11,
                                  color: "#4b5563",
                                  fontWeight: 500,
                                }}
                              >
                                /each Tab
                              </Typography>
                            </Box>
                          )}

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                          
                           {medicine.selling_unit=="loose_allowed"?
                          (<Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                            MRP ₹{(medicine.price*medicine.pack_size)?.toFixed(2)}
                             <Typography 
                                component="span"
                                sx={{
                                  ml: 0.3,
                                  fontSize: 12,
                                  color: "#3b3f44",
                                  fontWeight: 500,
                                }}> /{medicine.pack_size} Tab</Typography>
                          </Typography>):(
                            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                            MRP ₹{medicine.price?.toFixed(2)}
                          </Typography>
                          )}
                          {medicine.base_price > 0 && medicine.price > medicine.base_price && (() => {
                            const maxDiscountPct = ((medicine.price - medicine.base_price) / medicine.price) * 100;
                            return (
                              <Chip
                                label={
                                  offeredDiscountPct !== null
                                    ? `Up to ${offeredDiscountPct}% OFF`
                                    : `Up to ${Math.floor(maxDiscountPct)}% OFF`
                                }
                                size="small"
                                sx={{ bgcolor: '#fff7ed', color: '#ea580c', fontWeight: 700, height: 20, fontSize: 11, border: '1px solid #fed7aa', pointerEvents: 'none' }}
                              />
                            );
                          })()}
                        </Box>
                        {medicine.selling_unit=="loose_allowed"&&(<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        <InfoOutlined sx={{ fontSize: 14, color: "#0284c7" }} />
                        <Typography sx={{ fontSize: 11, color: "#475569" }}>
                          1 in cart = 1 tab
                        </Typography>
                      </Box>)}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Chip label={medicine.drug_type} size="small" sx={{ bgcolor: '#dbeafe', color: '#006d77', fontSize: 12, fontWeight: 500, pointerEvents: 'none' }} />
                          <Typography variant="caption" sx={{ fontSize: 12, color: '#16a34a', fontWeight: 500 }}>In Stock</Typography>
                        </Box>

                        {medicine.prescription_required && (
                          <Chip label="Prescription Required" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 11, fontWeight: 600, mb: 2, width: '100%', pointerEvents: 'none' }} />
                        )}

                        <Box sx={{ mt: 'auto' }}>
                          {quantity === 0 ? (
                            <Button
                              fullWidth variant="contained" startIcon={<Add />}
                              onClick={() => addToCart(medicine)}
                              sx={{ bgcolor: '#006D77', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.2, borderRadius: 2, '&:hover': { bgcolor: '#005560' }, '&:disabled': { bgcolor: '#cbd5e1', color: '#fff' } }}
                            >
                              Add to Cart
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, bgcolor: '#f1f5f9', borderRadius: 2, p: 1 }}>
                              <IconButton size="small" onClick={() => removeFromCart(medicine.medicine_id)} sx={{ bgcolor: '#fff', boxShadow: 1, '&:hover': { bgcolor: '#e2e8f0' } }}>
                                <Remove sx={{ fontSize: 18, color: '#006d77' }} />
                              </IconButton>
                              <TextField
                                size="small" type="number" value={quantity}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  // Allow empty value temporarily while typing
                                  if (value === '') {
                                    const updatedCart = cart.map(item =>
                                      item.medicine_id === medicine.medicine_id
                                        ? { ...item, quantity: '' }
                                        : item
                                    );

                                    setCart(updatedCart);
                                    return;
                                  }

                                  const numValue = parseInt(value);

                                  if (isNaN(numValue) || numValue < 1) return;

                                  const updatedCart = cart.map(item =>
                                    item.medicine_id === medicine.medicine_id
                                      ? { ...item, quantity: numValue }
                                      : item
                                  );

                                  setCart(updatedCart);
                                  sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const updatedCart = cart.map(item =>
                                      item.medicine_id === medicine.medicine_id
                                        ? { ...item, quantity: 1 }
                                        : item
                                    );

                                    setCart(updatedCart);
                                    sessionStorage.setItem('pharmacyCart', JSON.stringify(updatedCart));
                                  }
                                }}
                                inputProps={{ min: 1, style: { textAlign: 'center', fontSize: 16, fontWeight: 700, color: '#1e293b', padding: '6px 8px' } }}
                                sx={{
                                  width: 60, bgcolor: '#fff', borderRadius: 1,
                                  '& .MuiOutlinedInput-root': { '& fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: '2px solid #006d77' } },
                                  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                                  '& input[type=number]': { MozAppearance: 'textfield' },
                                }}
                              />
                              <IconButton size="small" onClick={() => addToCart(medicine)} sx={{ bgcolor: '#fff', boxShadow: 1, '&:hover': { bgcolor: '#e2e8f0' } }}>
                                <Add sx={{ fontSize: 18, color: '#006d77' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages} page={currentPage} onChange={handlePageChange}
                  color="primary" size="large" showFirstButton showLastButton
                  sx={{
                    '& .MuiPaginationItem-root': { fontSize: 16, fontWeight: 600 },
                    '& .Mui-selected': { bgcolor: '#006d77 !important', color: '#fff', '&:hover': { bgcolor: '#005560 !important' } },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* ─── Cart Footer ──────────────────────────────────────────────────────── */}
      {cart.length > 0 && (
        <Paper elevation={8} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: '#fff', borderTop: '2px solid #006d77', zIndex: 1000 }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                  {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'} in cart
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>
                  MRP ₹{mrpTotal.toFixed(2)}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#006d77' }}>
                  ₹{sellingTotal.toFixed(2)}
                </Typography>
                {hasAyuja && totalSavings > 0 && (
                  <Typography sx={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                    You save ₹{totalSavings.toFixed(2)}
                    {ayujaSummary?.final_discount_pct ? ` (${ayujaSummary.final_discount_pct.toFixed(1)}% OFF)` : ''}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained" endIcon={<ArrowForward />} onClick={proceedToCheckout}
                sx={{ bgcolor: '#006D77', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: 16, px: 4, py: 1.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,109,119,0.3)', '&:hover': { bgcolor: '#005560', boxShadow: '0 6px 16px rgba(0,109,119,0.4)' } }}
              >
                Proceed to Checkout
              </Button>
            </Box>
          </Container>
        </Paper>
      )}

      {/* Prescription Upload Dialog */}
      <Dialog open={showPrescriptionDialog} onClose={handleClosePrescriptionDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Upload Prescription</Typography>
          <IconButton onClick={handleClosePrescriptionDialog} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 2 }}>
            {prescriptionFile ? (
              <Box>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: '#16a34a', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>Prescription uploaded</Typography>
                    </Box>
                    <IconButton size="small" onClick={handleRemovePrescription} sx={{ color: '#ef4444' }}><Close fontSize="small" /></IconButton>
                  </Box>
                  {prescriptionPreview && !prescriptionPreview.startsWith('data:application/pdf') && (
                    <Box component="img" src={prescriptionPreview} alt="Prescription preview"
                      sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 1, border: '1px solid #e2e8f0' }}
                    />
                  )}
                  {prescriptionPreview?.startsWith('data:application/pdf') && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', borderRadius: 1, p: 3, border: '1px solid #e2e8f0' }}>
                      <Typography sx={{ fontSize: 14, color: '#64748b' }}>PDF Document Uploaded</Typography>
                    </Box>
                  )}
                </Paper>
                <Button fullWidth variant="outlined" component="label" startIcon={<Upload />}
                  sx={{ borderColor: '#006d77', color: '#006d77', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#005560', bgcolor: '#f0fdfa' } }}>
                  Upload Different File
                  <input type="file" hidden accept="image/*,.pdf" onChange={handlePrescriptionFileChange} />
                </Button>
              </Box>
            ) : (
              <Box>
                <Paper elevation={0} sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 2, border: '2px dashed #cbd5e1', textAlign: 'center', mb: 2 }}>
                  <Upload sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#475569', mb: 1 }}>Upload your prescription</Typography>
                  <Typography sx={{ fontSize: 13, color: '#64748b', mb: 3 }}>Accepted formats: JPG, PNG, PDF (Max 5MB)</Typography>
                  <Button variant="contained" component="label" startIcon={<Upload />}
                    sx={{ bgcolor: '#006d77', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, '&:hover': { bgcolor: '#005560' } }}>
                    Choose File
                    <input type="file" hidden accept="image/*,.pdf" onChange={handlePrescriptionFileChange} />
                  </Button>
                </Paper>
                <Alert severity="info" sx={{ fontSize: 13 }}>
                  <strong>Note:</strong> Prescription is required for certain medicines. Please ensure your prescription is clear and readable.
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePrescriptionDialog} variant="contained" fullWidth
            sx={{ bgcolor: '#006d77', color: '#fff', textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { bgcolor: '#005560' } }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 3 }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

            <Drawer
        anchor="right"
        open={showCart}
        onClose={() => setShowCart(false)}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: false
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            height:"95%",
            mt:2,
            p: 2,
            bgcolor: '#f8fafc'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Cart
          </Typography>
      
          <IconButton onClick={() => setShowCart(false)}>
            <Close />
          </IconButton>
        </Box>
      
        {cart.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 10
            }}
          >
            <ShoppingCart sx={{ fontSize: 80, color: '#cbd5e1' }} />
      
            <Typography sx={{ mt: 2, color: '#64748b' }}>
              Your cart is empty
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ flex: 1 }}>
              {cart.map((item) => (
                <Paper
                  key={item.medicine_id}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                      src={item.image_url}
                      variant="rounded"
                      sx={{ width: 60, height: 60 }}
                    />
      
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: 14
                        }}
                      >
                        {item.name}
                      </Typography>
      
                      <Typography
                        sx={{
                          color: '#64748b',
                          fontSize: 13,
                          mt: 0.5
                        }}
                      >
                        ₹
                        {(
                          item.price
                        ).toFixed(2)}
                      </Typography>
      
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() =>
                            removeFromCart(item.medicine_id)
                          }
                          sx={{
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
      
                        <Typography sx={{ fontWeight: 700 }}>
                          {item.quantity}
                        </Typography>
      
                        <IconButton
                          size="small"
                          onClick={() => addToCart(item)}
                          sx={{
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>
      
            <Divider sx={{ my: 2 }} />
      
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1
                }}
              >
                <span>MRP Total</span>
                <strong>₹{mrpTotal.toFixed(2)}</strong>
              </Typography>
      
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: '#16a34a',
                  fontWeight: 600
                }}
              >
                <span>You Save</span>
                <span>₹{totalSavings.toFixed(2)}</span>
              </Typography>
      
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#006d77',
                  mt: 1
                }}
              >
                <span>Total</span>
                <span>₹{sellingTotal.toFixed(2)}</span>
              </Typography>
            </Box>
      
            <Button
              fullWidth
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => {
                setShowCart(false);
      
                if (!patientDetails) {
                  setShowPatientForm(true);
                } else {
                  proceedToCheckout();
                }
              }}
              sx={{
                bgcolor: '#006d77',
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: '#005560'
                }
              }}
            >
              Proceed
            </Button>
          </>
        )}
      </Drawer>
      
    </Box>
  );
};

export default MedicineListScreen;